"use client";

import { memo, useCallback, useEffect, useMemo, useState } from "react";
import DatePicker from "react-datepicker";

import "react-datepicker/dist/react-datepicker.css";

import { Button, Dialog, DialogContent, DialogTitle } from "ui";

import {
  clampToMinDate,
  normalizeToNextQuarterHour,
} from "@/lib/utils/datetime";

interface SchedulePickerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Earliest selectable date. Defaults to "now" rounded to next quarter hour. */
  minDate?: Date;
  /** Initial date shown in the picker. Defaults to `minDate`. */
  defaultDate?: Date;
  /** Called with the chosen date when the user confirms. The component
   *  does NOT close itself on confirm — the consumer decides. */
  onConfirm: (date: Date) => void;
}

function isPastDay(date: Date, minDate: Date): boolean {
  const startOfMinDay = new Date(minDate);
  startOfMinDay.setHours(0, 0, 0, 0);
  return date.getTime() < startOfMinDay.getTime();
}

function SchedulePickerModalComponent({
  open,
  onOpenChange,
  minDate,
  defaultDate,
  onConfirm,
}: SchedulePickerModalProps) {
  const effectiveMinDate = useMemo(
    () => normalizeToNextQuarterHour(minDate ?? new Date()),
    [minDate],
  );

  const initialDate = useMemo(
    () => defaultDate ?? effectiveMinDate,
    [defaultDate, effectiveMinDate],
  );

  const [selectedDate, setSelectedDate] = useState<Date>(initialDate);

  // Reset internal state each time the modal is opened so it reflects a fresh
  // "now" baseline rather than whatever the user picked last time.
  useEffect(() => {
    if (open) {
      setSelectedDate(initialDate);
    }
  }, [open, initialDate]);

  const handleDateChange = useCallback(
    (date: Date | null) => {
      if (!date) return;
      const normalized = normalizeToNextQuarterHour(date);
      setSelectedDate(clampToMinDate(normalized, effectiveMinDate));
    },
    [effectiveMinDate],
  );

  const getMinTime = useCallback((): Date => {
    if (!selectedDate) return new Date(0, 0, 0, 0, 0);

    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const startOfSelected = new Date(selectedDate);
    startOfSelected.setHours(0, 0, 0, 0);

    // If the selected date is today, clamp times to the min-date lower bound.
    if (startOfSelected.getTime() === startOfToday.getTime()) {
      return effectiveMinDate;
    }

    return new Date(0, 0, 0, 0, 0);
  }, [selectedDate, effectiveMinDate]);

  const filterTime = useCallback(
    (time: Date): boolean => {
      if (!selectedDate) return true;

      const startOfToday = new Date();
      startOfToday.setHours(0, 0, 0, 0);
      const startOfSelected = new Date(selectedDate);
      startOfSelected.setHours(0, 0, 0, 0);

      if (startOfSelected.getTime() === startOfToday.getTime()) {
        const timeHours = time.getHours();
        const timeMinutes = time.getMinutes();
        const minHours = effectiveMinDate.getHours();
        const minMinutes = effectiveMinDate.getMinutes();

        if (timeHours > minHours) return true;
        if (timeHours === minHours && timeMinutes >= minMinutes) return true;
        return false;
      }

      return true;
    },
    [selectedDate, effectiveMinDate],
  );

  const handleConfirm = useCallback(() => {
    if (!selectedDate) return;
    onConfirm(selectedDate);
  }, [selectedDate, onConfirm]);

  // The react-datepicker calendar renders in a portal (`.react-datepicker-popper`)
  // outside the dialog's DOM subtree. With `modal={false}`, Radix treats clicks
  // on a day/time as "outside" the dialog and dismisses it before the selection
  // lands. Keep the dialog open whenever the interaction happens inside the
  // datepicker popper. Radix dispatches a CustomEvent whose `detail.originalEvent`
  // holds the real DOM event — `event.target` is the dialog content, not the click.
  const handleInteractOutside = useCallback((event: Event) => {
    const target = (event as CustomEvent<{ originalEvent?: Event }>).detail
      ?.originalEvent?.target as Element | null;
    if (target?.closest(".react-datepicker-popper")) {
      event.preventDefault();
    }
  }, []);

  // This dialog is opened from a DropdownMenuItem. When that menu closes, Radix's
  // FocusScope restores focus to the Publish trigger (outside this dialog), which
  // fires a focus-outside dismiss and flickers the dialog shut before the user can
  // pick a date. Focus leaving the dialog is never a reason to discard a scheduling
  // intent, so always keep it open on focus-outside (Escape / outside click still close it).
  const handleFocusOutside = useCallback((event: Event) => {
    event.preventDefault();
  }, []);

  return (
    // `modal={false}` works around a React 19 + Radix Dialog bug where the
    // body ends up stuck with `pointer-events: none` after close.
    <Dialog open={open} onOpenChange={onOpenChange} modal={false}>
      <DialogContent
        className="max-w-md border-border bg-card p-6 text-foreground"
        onInteractOutside={handleInteractOutside}
        onFocusOutside={handleFocusOutside}
      >
        <DialogTitle className="mb-4 text-lg font-medium text-foreground">
          Schedule Post
        </DialogTitle>
        <div className="flex w-full flex-col gap-4">
          <DatePicker
            selected={selectedDate}
            onChange={handleDateChange}
            minDate={effectiveMinDate}
            minTime={getMinTime()}
            maxTime={new Date(0, 0, 0, 23, 59)}
            filterTime={filterTime}
            showTimeSelect
            timeFormat="HH:mm"
            timeIntervals={15}
            timeInputLabel="Time:"
            dateFormat="MMMM d, yyyy h:mm aa"
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            popperClassName="z-[999]"
            calendarClassName="rounded-md border border-border bg-popover text-popover-foreground"
            weekDayClassName={() => "text-muted-foreground"}
            monthClassName={() => "text-popover-foreground"}
            dayClassName={(date) =>
              isPastDay(date, effectiveMinDate)
                ? "cursor-not-allowed text-muted-foreground opacity-40 hover:bg-transparent"
                : "text-popover-foreground hover:bg-accent hover:text-accent-foreground"
            }
          />
          <Button
            variant="default"
            className="w-full bg-primary p-4 text-primary-foreground"
            onClick={handleConfirm}
            disabled={!selectedDate}
          >
            Schedule
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export const SchedulePickerModal = memo(SchedulePickerModalComponent);
